import { create } from "zustand"
import { useAuthStore } from "./auth.store"
import {
  ChatError,
  RATE_LIMIT_GUEST_MESSAGE,
  RATE_LIMIT_MESSAGE,
  approveAction,
  cancelRun,
  createConversation,
  listMessages,
  regenerateMessage,
  rejectAction,
  sendMessage,
  submitToolResult,
  sendFeedback,
} from "@/lib/api/chat.api"
import { streamRun } from "@/lib/api/chat.sse"
import {
  buildPageContextResult,
  isNavigateTool,
  isPageContextTool,
  toNavigationIntent,
} from "@/lib/api/chat.helpers"
import { validateNavigation, NAVIGATE_UI_TOOL, GET_PAGE_CONTEXT_TOOL } from "@/lib/navigation"
import type {
  ApprovalRequiredData,
  FeedbackRating,
  Message,
  NavigationIntent,
  SendMessageResult,
  StreamItem,
  ToolActivity,
} from "@/lib/api/chat.types"

const generateUUID = () => {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID()
  }
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0
    const v = c === "x" ? r : (r & 0x3) | 0x8
    return v.toString(16)
  })
}

// The active backend conversation id for this widget, persisted in
// sessionStorage so history survives a reload within the same tab.
const CONVERSATION_ID_KEY = "enga-copilot-conversation-id"

function readConversationId(): string | null {
  if (typeof sessionStorage === "undefined") return null
  const id = sessionStorage.getItem(CONVERSATION_ID_KEY)
  return id && id !== "undefined" && id !== "null" ? id : null
}

function writeConversationId(id: string | null) {
  if (typeof sessionStorage === "undefined") return
  if (id) sessionStorage.setItem(CONVERSATION_ID_KEY, id)
  else sessionStorage.removeItem(CONVERSATION_ID_KEY)
}

/**
 * Only anonymous guests persist their active conversation to sessionStorage —
 * it's the per-tab "pickup" id. Signed-in conversations live server-side and
 * come from the auth-owned list, so storing one here would risk leaking a guest
 * id across identities.
 */
const isGuestSession = () => !useAuthStore.getState().accessToken

/** Abort handle for the in-flight SSE stream so stop/cancel closes it promptly. */
let activeStreamController: AbortController | null = null

function upsertTool(activities: ToolActivity[], tool: ToolActivity): ToolActivity[] {
  const idx = activities.findIndex((t) => t.id === tool.id)
  if (idx === -1) return [...activities, tool]
  const next = [...activities]
  next[idx] = tool
  return next
}

/**
 * Ordered ChatGPT-style stream helpers. Tool calls stay interleaved with the
 * assistant's text (coalescing trailing deltas into one text item) so they
 * render exactly where the model invoked them.
 */
function appendTextItem(timeline: StreamItem[], content: string): StreamItem[] {
  const last = timeline[timeline.length - 1]
  if (last && last.kind === "text") {
    return [...timeline.slice(0, -1), { kind: "text", content: last.content + content }]
  }
  return [...timeline, { kind: "text", content }]
}

function upsertToolItem(timeline: StreamItem[], tool: ToolActivity): StreamItem[] {
  const idx = timeline.findIndex((item) => item.kind === "tool" && item.tool.id === tool.id)
  if (idx === -1) return [...timeline, { kind: "tool", tool }]
  return [...timeline.slice(0, idx), { kind: "tool", tool }, ...timeline.slice(idx + 1)]
}

/** Stable key for a navigation intent so duplicate tool events can be ignored. */
function navigationIntentKey(intent: NavigationIntent): string {
  return `${intent.page ?? ""}|${JSON.stringify(intent.params ?? {})}|${JSON.stringify(intent.filters ?? {})}`
}

/** Keys of navigation intents already executed so tool.completed replays don't re-prompt. */
let executedNavKey: string | null = null

interface CopilotState {
  isOpen: boolean
  messages: Message[]
  isStreaming: boolean
  streamingText: string
  streamingReasoning: string
  currentProgressStep: string | null
  connectionStatus: "CONNECTED" | "CONNECTING" | "OFFLINE"
  unreadCount: number
  isTooltipDismissed: boolean
  lastVisitPath: string
  toolActivities: ToolActivity[]
  streamTimeline: StreamItem[]
  pendingApproval: ApprovalRequiredData | null
  pendingNavigation: NavigationIntent | null
  navigationRequest: string | null
  runId: string | null
  conversationId: string | null
  isHydrating: boolean

  toggleOpen: () => void
  setOpen: (open: boolean) => void
  send: (content: string, model?: string) => Promise<void>
  regenerate: (messageId: string) => Promise<void>
  attachRun: (runId: string) => Promise<void>
  streamFailed: (message: string) => void
  stopStreaming: () => void
  decideApproval: (actionId: string, approve: boolean) => Promise<void>
  approveNavigation: () => Promise<void>
  rejectNavigation: () => Promise<void>
  setNavigationRequest: (url: string | null) => void
  rateMessage: (messageId: string, rating: FeedbackRating) => Promise<void>
  setConversationId: (id: string | null) => void
  hydrate: () => Promise<void>
  reloadMessages: () => Promise<void>
  newConversation: () => void
  clearConversation: () => void
  setTooltipDismissed: (dismissed: boolean) => void
  setLastVisitPath: (path: string) => void
  setConnectionStatus: (status: "CONNECTED" | "CONNECTING" | "OFFLINE") => void
  incrementUnread: () => void
  resetUnread: () => void
}

export const useCopilotStore = create<CopilotState>()((set, get) => {
  return {
    isOpen: false,
    messages: [],
    isStreaming: false,
    streamingText: "",
    streamingReasoning: "",
    currentProgressStep: null,
    connectionStatus: "CONNECTED",
    unreadCount: 0,
    isTooltipDismissed: false,
    lastVisitPath: "",
    toolActivities: [],
    streamTimeline: [],
    pendingApproval: null,
    pendingNavigation: null,
    navigationRequest: null,
    runId: null,
    conversationId: readConversationId(),
    isHydrating: !!readConversationId(),

    toggleOpen: () => {
      const nextState = !get().isOpen
      set({ isOpen: nextState })
      if (nextState) {
        get().resetUnread()
        void get().hydrate()
      }
    },

    setOpen: (open) => {
      set({ isOpen: open })
      if (open) {
        get().resetUnread()
        void get().hydrate()
      }
    },

    send: async (content, model) => {
      // Abort any in-flight stream before starting a new one.
      get().stopStreaming()
      executedNavKey = null
      set({
        isStreaming: true,
        streamingText: "",
        streamingReasoning: "",
        currentProgressStep: "Connecting...",
        pendingApproval: null,
        pendingNavigation: null,
        toolActivities: [],
        streamTimeline: [],
        messages: [
          ...get().messages,
          {
            id: `tmp-${generateUUID()}`,
            role: "USER",
            content,
            createdAt: new Date().toISOString(),
          },
        ],
      })

      let conversationId = get().conversationId
      const persistForGuest = () => {
        if (isGuestSession()) writeConversationId(conversationId)
      }
      try {
        if (!conversationId) {
          const conv = await createConversation()
          conversationId = conv.id
          persistForGuest()
          set({ conversationId: conv.id })
        }

        let result: SendMessageResult
        try {
          result = await sendMessage(conversationId, { content, model, stream: true, tools: [NAVIGATE_UI_TOOL, GET_PAGE_CONTEXT_TOOL] })
        } catch (err) {
          // Conversation may have been deleted server-side (e.g. from /chat) — recreate.
          if (err instanceof ChatError && err.status === 404) {
            const conv = await createConversation()
            conversationId = conv.id
            persistForGuest()
            set({ conversationId: conv.id })
            result = await sendMessage(conversationId, { content, model, stream: true, tools: [NAVIGATE_UI_TOOL, GET_PAGE_CONTEXT_TOOL] })
          } else {
            throw err
          }
        }

        set((state) => ({
          messages: state.messages.map((msg) =>
            msg.id.startsWith("tmp-") && msg.role === "USER" && msg.content === content
              ? { ...msg, id: result.messageId }
              : msg
          ),
        }))

        await get().attachRun(result.runId)
      } catch (err) {
        // 429 = rate limited. Never auto-retry — show the funnel-friendly message.
        if (err instanceof ChatError && err.status === 429) {
          get().streamFailed(isGuestSession() ? RATE_LIMIT_GUEST_MESSAGE : RATE_LIMIT_MESSAGE)
          return
        }
        get().streamFailed(`Failed to start request`)
      }
    },

    regenerate: async (messageId) => {
      get().stopStreaming()
      const conversationId = get().conversationId
      if (!conversationId) return

      executedNavKey = null
      set({
        isStreaming: true,
        streamingText: "",
        streamingReasoning: "",
        currentProgressStep: "Regenerating...",
        pendingApproval: null,
        pendingNavigation: null,
        toolActivities: [],
        streamTimeline: [],
      })

      try {
        const result = await regenerateMessage(conversationId, messageId, {
          stream: true,
          tools: [NAVIGATE_UI_TOOL, GET_PAGE_CONTEXT_TOOL],
        })
        await get().attachRun(result.runId)
      } catch (err) {
        if (err instanceof ChatError && err.status === 429) {
          get().streamFailed(isGuestSession() ? RATE_LIMIT_GUEST_MESSAGE : RATE_LIMIT_MESSAGE)
          return
        }
        get().streamFailed(`Failed to regenerate: ${err instanceof Error ? err.message : String(err)}`)
      }
    },

    attachRun: async (runId) => {
      // Replace any in-flight stream so a stopped run frees its slot promptly.
      activeStreamController?.abort()
      const controller = new AbortController()
      activeStreamController = controller
      set({ runId, currentProgressStep: null })

      try {
        await streamRun(
          runId,
          {
            onDelta: (c) =>
              set({
                streamingText: get().streamingText + c,
                currentProgressStep: null,
                streamTimeline: appendTextItem(get().streamTimeline, c),
              }),
            onReasoning: (c) =>
              set({
                streamingReasoning: get().streamingReasoning + c,
                currentProgressStep: null,
              }),
            onTool: (tool) => {
              set({ streamTimeline: upsertToolItem(get().streamTimeline, tool) })
              if (isNavigateTool(tool.name)) {
                const intent = toNavigationIntent(tool.name, tool.arguments)
                if (intent.page && executedNavKey !== navigationIntentKey(intent)) {
                  set({ pendingNavigation: intent })
                }
                return
              }
              set({ toolActivities: upsertTool(get().toolActivities, tool) })
            },
            onApproval: (approval) => {
              if (isNavigateTool(approval.toolName)) {
                const intent = toNavigationIntent(approval.toolName, approval.arguments, approval.actionId)
                if (intent.page && executedNavKey !== navigationIntentKey(intent)) {
                  set({ pendingNavigation: intent })
                }
                return
              }
              set({ pendingApproval: approval })
            },
            onToolResultRequired: (resultRequired) => {
              if (!isPageContextTool(resultRequired.toolName)) return
              submitToolResult(runId, resultRequired.actionId, buildPageContextResult(resultRequired.arguments)).catch(
                () => undefined
              )
            },
            onDone: async (final) => {
              executedNavKey = null
              const text = get().streamingText
              const reason = get().streamingReasoning

              set({
                isStreaming: false,
                streamingText: "",
                streamingReasoning: "",
                currentProgressStep: null,
                runId: null,
                pendingApproval: null,
                pendingNavigation: null,
                toolActivities: [],
                streamTimeline: [],
              })

              if (text) {
                set((state) => ({
                  messages: [
                    ...state.messages,
                    {
                      id: final?.messageId || `asst-${Date.now()}`,
                      role: "ASSISTANT",
                      content: text,
                      meta: reason ? { reasoning: reason } : null,
                      createdAt: new Date().toISOString(),
                    },
                  ],
                }))
              }

              if (!get().isOpen) get().incrementUnread()
            },
            onError: (err) => {
              if (err instanceof ChatError && err.status === 429) {
                get().streamFailed(isGuestSession() ? RATE_LIMIT_GUEST_MESSAGE : RATE_LIMIT_MESSAGE)
                return
              }
              get().streamFailed(`Request failed: ${err.message}`)
            },
          },
          { signal: controller.signal, seen: new Set<number>() }
        )
      } finally {
        if (activeStreamController === controller) activeStreamController = null
      }
    },

    streamFailed: (message) => {
      executedNavKey = null
      const isFailedStart = message === "Failed to start request"
      set({
        isStreaming: false,
        streamingText: "",
        streamingReasoning: "",
        currentProgressStep: null,
        runId: null,
        pendingApproval: null,
        pendingNavigation: null,
        toolActivities: [],
        streamTimeline: [],
        messages: [
          ...get().messages,
          {
            id: `${isFailedStart ? "asst" : "sys"}-${generateUUID()}`,
            role: isFailedStart ? "ASSISTANT" : "SYSTEM",
            content: message,
            createdAt: new Date().toISOString(),
          },
        ],
      })
    },

    stopStreaming: () => {
      // Abort the in-flight SSE immediately so a cancelled guest run frees its
      // (max 2) stream slot right away instead of waiting for server disposal.
      activeStreamController?.abort()
      activeStreamController = null
      const state = get()
      if (state.runId) {
        cancelRun(state.runId).catch(() => undefined)
      }
      const partial = state.streamingText
      const rawReasoning = state.streamingReasoning
      executedNavKey = null

      set({
        isStreaming: false,
        streamingText: "",
        streamingReasoning: "",
        currentProgressStep: null,
        runId: null,
        pendingApproval: null,
        pendingNavigation: null,
        toolActivities: [],
        streamTimeline: [],
      })

      if (partial) {
        set({
          messages: [
            ...get().messages,
            {
              id: `partial-${generateUUID()}`,
              role: "ASSISTANT",
              content: partial,
              meta: rawReasoning ? { reasoning: rawReasoning } : null,
              createdAt: new Date().toISOString(),
            },
          ],
        })
      }
    },

    decideApproval: async (actionId, approve) => {
      const runId = get().runId
      if (runId) {
        try {
          if (approve) await approveAction(runId, actionId)
          else await rejectAction(runId, actionId)
        } catch {
          // Stream will settle the run state.
        }
      }
      set({ pendingApproval: null })
    },

    approveNavigation: async () => {
      const nav = get().pendingNavigation
      if (!nav) return
      const runId = get().runId
      if (nav.actionId && runId) {
        try {
          await approveAction(runId, nav.actionId)
        } catch {
          // Auto-approved server-side already — navigation is still gated here.
        }
      }
      set({ pendingNavigation: null })

      const role = useAuthStore.getState().user?.role
      const result = validateNavigation(
        { page: nav.page ?? "", params: nav.params, filters: nav.filters },
        role
      )
      if ("url" in result) {
        executedNavKey = navigationIntentKey(nav)
        set({ navigationRequest: result.url })
      } else {
        set({
          messages: [
            ...get().messages,
            {
              id: `sys-${generateUUID()}`,
              role: "SYSTEM",
              content: `Cannot navigate: ${result.error}`,
              createdAt: new Date().toISOString(),
            },
          ],
        })
      }
    },

    rejectNavigation: async () => {
      const nav = get().pendingNavigation
      const runId = get().runId
      if (nav?.actionId && runId) {
        try {
          await rejectAction(runId, nav.actionId)
        } catch {
          // ignore
        }
      }
      set({ pendingNavigation: null })
    },

    setNavigationRequest: (url) => set({ navigationRequest: url }),

    setConversationId: (id) => {
      if (id === get().conversationId) return
      writeConversationId(id)
      set({
        conversationId: id,
        messages: [],
        toolActivities: [],
        streamTimeline: [],
        isStreaming: false,
        isHydrating: !!id,
      })
      if (id) {
        void get().hydrate()
      }
    },

    rateMessage: async (messageId: string, rating: FeedbackRating) => {
      set((state) => ({
        messages: state.messages.map((msg) =>
          msg.id === messageId ? { ...msg, feedbackRating: rating } : msg
        ),
      }))
      try {
        await sendFeedback(messageId, rating)
      } catch {
        // ignore
      }
    },

    hydrate: async () => {
      const conversationId = get().conversationId
      if (!conversationId) {
        set({ isHydrating: false })
        return
      }
      if (get().messages.length > 0) {
        set({ isHydrating: false })
        return
      }
      set({ isHydrating: true })
      try {
        const { data } = await listMessages(conversationId, { limit: 100 })
        if (data.length) set({ messages: data })
      } catch (err) {
        if (err instanceof ChatError && (err.status === 404 || err.status === 401)) {
          writeConversationId(null)
          set({ conversationId: null })
        }
      } finally {
        set({ isHydrating: false })
      }
    },

    reloadMessages: async () => {
      const conversationId = get().conversationId
      if (!conversationId) return
      try {
        const { data } = await listMessages(conversationId, { limit: 100 })
        if (data.length) set({ messages: data })
      } catch {
        // keep the optimistic state
      }
    },

    newConversation: () => {
      writeConversationId(null)
      executedNavKey = null
      set({
        messages: [],
        toolActivities: [],
        streamTimeline: [],
        isStreaming: false,
        streamingText: "",
        streamingReasoning: "",
        currentProgressStep: null,
        runId: null,
        pendingApproval: null,
        pendingNavigation: null,
        navigationRequest: null,
        conversationId: null,
        isHydrating: false,
      })
    },

    clearConversation: () => get().newConversation(),

    setTooltipDismissed: (dismissed) => set({ isTooltipDismissed: dismissed }),
    setLastVisitPath: (path) => set({ lastVisitPath: path }),
    setConnectionStatus: (status) => set({ connectionStatus: status }),
    incrementUnread: () => set({ unreadCount: get().unreadCount + 1 }),
    resetUnread: () => set({ unreadCount: 0 }),
  }
})

// Reset the copilot session when the user logs out.
useAuthStore.subscribe((state) => {
  if (!state.isAuthenticated) {
    const copilot = useCopilotStore.getState()
    copilot.clearConversation()
    copilot.setOpen(false)
    copilot.setTooltipDismissed(true)
  }
})
